import React, { memo } from "react";
import { useTranslation } from "react-i18next";
import { MonitorIcon, MoonIcon, SunIcon } from "@phosphor-icons/react";
import { useAppContext } from "../../../app_context";
import { useTooltip } from "../tooltip";
import { nextThemePreference, type ThemePreference } from "../../utils/theme_preference";

const MODES: Record<ThemePreference, { Icon: typeof SunIcon; labelKey: string }> = {
	LIGHT: { Icon: SunIcon, labelKey: "app-header.settings.theme-light" },
	DARK: { Icon: MoonIcon, labelKey: "app-header.settings.theme-dark" },
	SYSTEM: { Icon: MonitorIcon, labelKey: "app-header.settings.theme-system" },
};

const BUTTON_CLASS = [
	"w-9 h-9 rounded-md inline-flex items-center justify-center cursor-pointer",
	"text-[#9797a6] hover:bg-[rgba(127,127,140,0.12)]",
].join(" ");

type ThemeSwitcherProps = {
	className?: string;
};

// Each tap moves to the next mode: light → dark → device → light. The icon shows the current one.
const ThemeSwitcher = ({ className = "" }: ThemeSwitcherProps) => {
	const { t } = useTranslation();
	const tip = useTooltip();
	const { appThemePreference, setAppThemePreference } = useAppContext();

	const { Icon, labelKey } = MODES[appThemePreference as ThemePreference] ?? MODES.LIGHT;
	const label = `${t("app-header.settings.theme")}: ${t(labelKey)}`;

	return (
		<button
			type="button"
			className={`${BUTTON_CLASS} ${className}`}
			onClick={() => setAppThemePreference(nextThemePreference)}
			aria-label={label}
			{...tip(label)}
			data-testid="theme-toggle-button"
			data-theme-mode={appThemePreference}
		>
			<Icon size={21} />
		</button>
	);
};

export default memo(ThemeSwitcher);
